import {castArray} from 'lodash';
import {DocumentStoreApi} from '@/components/document/store';
import {
  DependencyRegistrer,
  DocumentData,
  DocumentModel,
  ModelAction,
  ValueFactory,
  ValueResolver,
  VisibilitySet,
} from '@/engine';
import {logger} from '@/initializers/logger';
import {Action, TemplateValue, Reference} from '@/model/types';
import {ACTION_TYPES} from '@/model/types/constants';
import {BatchUpdate} from '@/types/document/store';
import {
  cancelAllocationAction,
  saveAllocationAction,
  computeAllocationAction,
} from './plugins/predetermined-allocations/actions';
import {createValueFactoryFromSwitch} from './processor/switch';
import {findMaxArrayIndex, findArrayGroupInModel, collectAllArrayFields} from './utils/array-fields';
import {recursiveReplaceIndex} from './utils/object';
import {extractDetailsFromAttachment} from './utils/ocr';

type ActionType = Action['type'];

export type ActionFactory<T extends ActionType> = (
  action: Extract<Action, {type: T}>,
  store: DocumentStoreApi,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
  documentModel?: DocumentModel,
  currentSection?: string,
  dependencyRegister?: DependencyRegistrer,
) => ModelAction;

type ActionMap = {
  [T in ActionType]: ActionFactory<T>;
};

const LogAction: ActionFactory<'log'> = action => {
  return (...args: unknown[]) => {
    const messages = action.messages.map(s => {
      if (typeof s !== 'string') return s;
      return s.replaceAll(/\$\d/g, match => {
        return (args as string[])[+match.substring(1)];
      });
    });

    logger.log(...messages);

    return true;
  };
};

const SetAction: ActionFactory<'set'> = (action, store, valueResolver, visibilitySet, _, __, dependencyRegistrer) => {
  let valueFactory: ValueFactory<string | TemplateValue | Reference> | undefined;

  if (typeof action.value !== 'string' && '$switch' in action.value) {
    valueFactory = createValueFactoryFromSwitch(action.value, '', {dependencyRegistrer});
  } else {
    const value = action.value;
    valueFactory = () => value;
  }

  return () => {
    const valueRef = valueFactory(store, valueResolver, visibilitySet);
    const value = valueResolver(valueRef, {
      keepDomainReference: true,
      visibilitySet: visibilitySet,
    });
    store.getState().updateField(action.field, value);
    return true;
  };
};

const UnsetAction: ActionFactory<'unset'> = (action, store) => {
  return () => {
    store.getState().unsetField(action.field);
    return true;
  };
};

const IncrementAction: ActionFactory<'increment'> = (action, store, valueResolver) => {
  return () => {
    const currentValue = Number(valueResolver({$ref: action.field}));
    if (isNaN(currentValue)) {
      throw new Error(`Field ${action.field} is not a number`);
    }
    const value = action.value ?? 1;
    store.getState().updateField(action.field, (currentValue + value).toFixed());
    return true;
  };
};

const DecrementAction: ActionFactory<'decrement'> = (action, store, valueResolver, visibilitySet) => {
  return () =>
    getAction({type: ACTION_TYPES.INCREMENT, field: action.field, value: -(action.value ?? 1)})(
      store,
      valueResolver,
      visibilitySet,
    )();
};

const AlertAction: ActionFactory<'alert'> = (action, _, valueResolver) => {
  return () => {
    alert(valueResolver(action.message));
    return true;
  };
};

const ConfirmAction: ActionFactory<'confirm'> = (action, store, valueResolver, visibilitySet) => {
  return () => {
    if (confirm(valueResolver(action.message))) {
      return (action.onConfirm ?? []).every(onConfirmAction =>
        getAction(onConfirmAction)(store, valueResolver, visibilitySet)(),
      );
    }
    return false;
  };
};

const newEntryAction: ActionFactory<'newEntry'> = (
  action,
  store,
  valueResolver,
  visibilitySet,
  _,
  __,
  dependencyRegistrer,
) => {
  const valueFactories = (action.initialValues ?? []).map(({field, value}) => ({
    field,
    factory:
      typeof value !== 'string' && '$switch' in value
        ? createValueFactoryFromSwitch(value, '', {dependencyRegistrer})
        : () => value,
  }));

  return () => {
    const arrayRowCountFieldId = `_${action.array}[count]`;
    const startRowIndex = Number(valueResolver({$ref: arrayRowCountFieldId}) ?? '0');
    const isBulkCreate = action.count !== undefined;
    const count = isBulkCreate ? Number(valueResolver(action.count) ?? '0') : 1;

    const updates: BatchUpdate[] = [];

    if (action.returnToGroupModal) {
      store.getState().setReturnToPreviousModalId(action.returnToGroupModal);
    }

    for (let offset = 0; offset < count; offset++) {
      const rowIndex = startRowIndex + offset;

      // Increment the row count
      updates.push({type: 'increment', id: arrayRowCountFieldId});

      const rowFieldIds: string[] = [];

      // Set all initial values for the fields in the row
      for (const {field, factory} of valueFactories) {
        const resolved = factory(store, valueResolver, visibilitySet);
        const [{field: id, value}] = recursiveReplaceIndex([{field, value: resolved}], rowIndex);

        // Rows created after the model was built are absent from the visibility set, resolving to ''
        visibilitySet.add(id);
        rowFieldIds.push(id);

        updates.push({type: 'set', id, value});
      }

      // The array processor only writes this on the next rebuild, until then array filters skip the row
      const rowFieldsId = `_${action.array}#${rowIndex}[fields]`;
      if (rowFieldIds.length > 0 && !valueResolver({$ref: rowFieldsId})) {
        updates.push({type: 'set', id: rowFieldsId, value: rowFieldIds.join(',')});
      }

      // Rows created in bulk (e.g. seeded from external data) should not open their edit modal
      if (!isBulkCreate) {
        updates.push({type: 'set', id: `_${action.array}#${rowIndex}[open]`, value: 'true'});
        updates.push({type: 'set', id: `_${action.array}#${rowIndex}[draft]`, value: '[]'});
        updates.push({type: 'set', id: `_${action.array}#${rowIndex}[new]`, value: 'true'});
      }
    }

    store.getState().batchUpdate(updates);

    return false;
  };
};

const resolveEditEntryRow = (
  action: Extract<Action, {type: 'editEntry'}>,
  valueResolver: ValueResolver,
): string | undefined => {
  if (typeof action.row === 'object' && '$lookup' in action.row) {
    const expectedValue = valueResolver(action.row.$lookup.value);
    const rowCount = Number(valueResolver({$ref: `_${action.array}[count]`}) ?? '0');

    for (let index = 0; index < rowCount; index++) {
      const field = action.row.$lookup.field.replaceAll('#{$index}', `#${index}`);
      const fieldValue = valueResolver({$ref: field});
      if (fieldValue === expectedValue) {
        return String(index);
      }
    }

    return undefined;
  }

  return action.row;
};

const editEntryAction: ActionFactory<'editEntry'> = (action, store, valueResolver) => {
  return () => {
    const row = resolveEditEntryRow(action, valueResolver);
    if (row === undefined) return false;

    if (action.returnToGroupModal) {
      store.getState().setReturnToPreviousModalId(action.returnToGroupModal);
    }

    const arrayRowStateFieldId = `_${action.array}#${row}`;

    const fields = valueResolver({$ref: `_${action.array}#${row}[fields]`}).split(',');

    const draft = [];

    for (const field of fields) {
      const value = valueResolver({$ref: field}, {keepReference: true});
      draft.push({type: 'set', id: field, value});
    }

    store.getState().batchUpdate([
      {type: 'set', id: `${arrayRowStateFieldId}[draft]`, value: JSON.stringify(draft)},
      {type: 'set', id: `${arrayRowStateFieldId}[open]`, value: 'true'},
      {type: 'set', id: `${arrayRowStateFieldId}[new]`, value: 'false'},
    ]);

    return false;
  };
};

const rollbackEntryToDraftAction: ActionFactory<'rollbackEntryToDraft'> = (action, store, valueResolver) => {
  return () => {
    const arrayRowStateFieldId = `_${action.array}#${action.row}`;
    const arrayRowStateDraftFieldId = `${arrayRowStateFieldId}[draft]`;
    let draft = [];

    try {
      draft = JSON.parse(valueResolver({$ref: arrayRowStateDraftFieldId}));
    } catch (error) {
      logger.warn(`Failed to parse JSON for draft at ${arrayRowStateDraftFieldId}:`, error);
      draft = [];
    }

    store
      .getState()
      .batchUpdate([
        ...draft,
        {type: 'set', id: arrayRowStateDraftFieldId, value: '[]'},
        {type: 'set', id: `${arrayRowStateFieldId}[open]`, value: 'false'},
      ]);

    return false;
  };
};

const saveEntryAction: ActionFactory<'saveEntry'> = (action, store) => {
  return () => {
    const arrayRowStateFieldId = `_${action.array}#${action.row}`;

    store.getState().batchUpdate([
      {type: 'set', id: `${arrayRowStateFieldId}[draft]`, value: '[]'},
      {type: 'set', id: `${arrayRowStateFieldId}[open]`, value: 'false'},
      {type: 'set', id: `${arrayRowStateFieldId}[new]`, value: 'false'},
    ]);

    return true;
  };
};

const reindexReferences = (
  data: DocumentData['data'],
  movedFieldIds: Map<string, string>,
  deletedFieldIds: Set<string>,
): BatchUpdate[] =>
  Object.entries(data).flatMap(([id, value]): BatchUpdate[] => {
    if (typeof value !== 'object' || !('$ref' in value) || deletedFieldIds.has(id)) return [];

    const targetId = movedFieldIds.get(id) ?? id;
    const movedRef = movedFieldIds.get(value.$ref);

    if (deletedFieldIds.has(value.$ref)) return [{type: 'set', id: targetId, value: ''}];

    if (movedRef) return [{type: 'set', id: targetId, value: {...value, $ref: movedRef}}];

    return [];
  });

const deleteEntryAction: ActionFactory<'deleteEntry'> = (action, store, valueResolver) => {
  return () => {
    const updates: BatchUpdate[] = [];

    const indexToDelete = Number(action.row);

    const fields = valueResolver({$ref: `_${action.array}#${indexToDelete}[fields]`}).split(',');

    // Unset all fields in the row
    for (const field of fields) {
      updates.push({type: 'unset', id: field});
    }

    // Unset the row internal fields
    updates.push({type: 'unset', id: `_${action.array}#${indexToDelete}[fields]`});
    updates.push({type: 'unset', id: `_${action.array}#${indexToDelete}[open]`});
    updates.push({type: 'unset', id: `_${action.array}#${indexToDelete}[draft]`});
    updates.push({type: 'unset', id: `_${action.array}#${indexToDelete}[new]`});

    // Decrement the row count
    const arrayRowCountFieldId = `_${action.array}[count]`;
    const currentCount = Number(valueResolver({$ref: arrayRowCountFieldId}));
    updates.push({type: 'decrement', id: arrayRowCountFieldId});

    // Move all rows after the deleted row down by one, keeping track of the new id of each moved field
    const movedFieldIds = new Map<string, string>();
    for (let index = indexToDelete; index < currentCount - 1; index++) {
      const nextRowFields = valueResolver({$ref: `_${action.array}#${index + 1}[fields]`}).split(',');
      const newRowFields = [];

      for (const field of nextRowFields) {
        const decrementedFieldId = field.replaceAll(/#(\d+)/g, (_, match) => `#${Number(match) - 1}`);
        movedFieldIds.set(field, decrementedFieldId);
        updates.push({type: 'move', fromId: field, toId: decrementedFieldId});
        newRowFields.push(decrementedFieldId);
      }

      // Move the row internal fields
      updates.push({
        type: 'move',
        fromId: `_${action.array}#${index + 1}[fields]`,
        toId: `_${action.array}#${index}[fields]`,
      });
      updates.push({
        type: 'move',
        fromId: `_${action.array}#${index + 1}[open]`,
        toId: `_${action.array}#${index}[open]`,
      });

      // Update the row internal fields with decremented fields ids
      updates.push({type: 'set', id: `_${action.array}#${index}[fields]`, value: newRowFields.join(',')});
    }

    // Update the refs pointing to moved fields (e.g. {$ref: 'J19#2'} -> {$ref: 'J19#1'}), other refs (e.g. COMP#0) stay valid
    updates.push(...reindexReferences(store.getState().data, movedFieldIds, new Set(fields)));

    store.getState().batchUpdate(updates);

    return true;
  };
};

const fillOcrAction: ActionFactory<'fillOcr'> = (action, store, valueResolver) => {
  return async () => {
    const updates: BatchUpdate[] = [];
    store.getState().batchUpdate([
      {type: 'set', id: `${action.field}_ocrSuccessMessage_input`, value: ''},
      {type: 'set', id: `${action.field}_ocrErrorMessage_input`, value: ''},
    ]);

    const attachmentId = valueResolver({$ref: action.field});
    const documentId = store.getState().meta.id;
    if (!documentId) return false;

    const resetActionFields = () => {
      for (const field of action.fields) {
        updates.push({type: 'set', id: field.field, value: ''});
      }
    };

    if (!attachmentId) {
      resetActionFields();
    } else {
      try {
        const extractedDetails = await extractDetailsFromAttachment(documentId, attachmentId);

        if (extractedDetails.account) {
          updates.push({type: 'set', id: `${action.field}_ocrSuccessMessage_input`, value: 'true'});

          for (const field of action.fields) {
            const value = extractedDetails[field.property];
            if (value) updates.push({type: 'set', id: field.field, value: value});
          }
        } else {
          resetActionFields();
          updates.push({type: 'set', id: `${action.field}_ocrErrorMessage_input`, value: 'true'});
        }
      } catch (error) {
        logger.error(error);
        resetActionFields();

        updates.push({type: 'set', id: `${action.field}_ocrErrorMessage_input`, value: 'true'});
      }
    }

    store.getState().batchUpdate(updates);

    return true;
  };
};

const clearEntriesAction: ActionFactory<'clearEntries'> = (
  action,
  store,
  valueResolver,
  visibilitySet,
  documentModel,
) => {
  return () => {
    const updates: BatchUpdate[] = [];
    for (const array of castArray(action.array)) {
      const arrayRowCountFieldId = `_${array}[count]`;

      // Reset the row count
      updates.push({type: 'set', id: arrayRowCountFieldId, value: '0'});

      // Find the actual maximum index in the data to handle overflow cases
      // Pass documentModel to also scan for template field patterns like J17#*, J18#*
      const maxIndex = findMaxArrayIndex(store.getState().data, array, documentModel);

      // If no elements exist, skip this array
      if (maxIndex < 0) {
        continue;
      }

      // Collect all field IDs that need to be cleared
      let allFieldIds: string[] = [];

      // If documentModel is available, use utility function to collect all fields
      if (documentModel) {
        const arrayGroup = findArrayGroupInModel(documentModel, array);
        if (arrayGroup) {
          allFieldIds = collectAllArrayFields(store.getState().data, arrayGroup, maxIndex);
        }
      }

      // If we couldn't use the utility (no documentModel or group not found),
      // fall back to collecting from [fields] metadata only
      if (allFieldIds.length === 0) {
        const allFieldIdsSet = new Set<string>();
        for (let index = 0; index <= maxIndex; index++) {
          const fieldsString = valueResolver({$ref: `_${array}#${index}[fields]`});
          if (fieldsString) {
            const fields = fieldsString.split(',');
            fields.forEach(field => allFieldIdsSet.add(field));
          }
        }
        allFieldIds = Array.from(allFieldIdsSet);
      }

      // Unset all collected fields
      allFieldIds.forEach(fieldId => {
        visibilitySet.delete(fieldId);
        updates.push({type: 'unset', id: fieldId});
      });

      // Unset the row internal fields for ALL found indices (including orphans)
      for (let index = 0; index <= maxIndex; index++) {
        updates.push({type: 'unset', id: `_${array}#${index}[fields]`});
        updates.push({type: 'unset', id: `_${array}#${index}[open]`});
      }
    }

    store.getState().batchUpdate(updates);

    return true;
  };
};

const openModalAction: ActionFactory<'openModal'> = (
  action,
  store,
  valueResolver,
  visibilitySet,
  _,
  __,
  dependencyRegistrer,
) => {
  const valueFactories = (action.initialValues ?? []).map(({field, value}) => ({
    field,
    factory:
      typeof value !== 'string' && '$switch' in value
        ? createValueFactoryFromSwitch(value, '', {dependencyRegistrer})
        : () => value,
  }));

  return () => {
    const updates: BatchUpdate[] = [];

    // Set the group open to true
    updates.push({type: 'set', id: `_${action.group}[open]`, value: 'true'});

    // Set all initial values for the fields in the group
    for (const {field, factory} of valueFactories) {
      updates.push({type: 'set', id: field, value: factory(store, valueResolver, visibilitySet)});
    }

    store.getState().batchUpdate(updates);

    return true;
  };
};

const closeModalAction: ActionFactory<'closeModal'> = (action, store) => {
  return () => {
    const updates: BatchUpdate[] = [];

    // Set the group open to false
    updates.push({type: 'set', id: `_${action.group}[open]`, value: 'false'});

    store.getState().batchUpdate(updates);

    return true;
  };
};

// ArraySetAction does the same as SetAction, as it is preprocessed to be duplicated for each row
// in the array, so the index are correctly identified and the SetAction can be used
const ArraySetAction: ActionFactory<'arraySet'> = (action, ...args) => {
  return () => {
    return getAction({type: ACTION_TYPES.SET, field: action.field, value: action.value})(...args)();
  };
};

const batchAction: ActionFactory<'batch'> = (action, ...args) => {
  return () => {
    return action.actions.every(subAction => getAction(subAction)(...args)());
  };
};

const actionMap: ActionMap = {
  set: SetAction,
  unset: UnsetAction,
  log: LogAction,
  alert: AlertAction,
  confirm: ConfirmAction,
  increment: IncrementAction,
  decrement: DecrementAction,
  newEntry: newEntryAction,
  editEntry: editEntryAction,
  deleteEntry: deleteEntryAction,
  saveEntry: saveEntryAction,
  rollbackEntryToDraft: rollbackEntryToDraftAction,
  clearEntries: clearEntriesAction,
  fillOcr: fillOcrAction,
  openModal: openModalAction,
  closeModal: closeModalAction,
  computeAllocation: computeAllocationAction,
  saveAllocation: saveAllocationAction,
  cancelAllocation: cancelAllocationAction,
  arraySet: ArraySetAction,
  batch: batchAction,
};

export const getAction = <K extends ActionType>(action: Extract<Action, {type: K}>) => {
  if (!actionMap[action.type]) {
    throw new Error(`Unknown action type: ${action.type}`);
  }

  return (
    store: DocumentStoreApi,
    valueResolver: ValueResolver,
    visibilitySet: VisibilitySet,
    documentModel?: DocumentModel,
    currentSection?: string,
    dependencyRegister?: DependencyRegistrer,
  ) => {
    return actionMap[action.type](
      action,
      store,
      valueResolver,
      visibilitySet,
      documentModel,
      currentSection,
      dependencyRegister,
    );
  };
};
